import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import {
  CalendarCheck,
  Star,
  Truck,
  UsersRound,
  Utensils,
  UtensilsCrossed,
  Wine,
  type LucideIcon,
} from "lucide-react";

import { HomeCompactFaq } from "@/components/home/home-compact-faq";
import { HomeInlineOfferCapture } from "@/components/home/home-inline-offer-capture";
import { HomepageAnalytics } from "@/components/home/homepage-analytics";
import { RomaDarkFunnelMobileBar } from "@/components/roma/dark-funnel/roma-dark-funnel-mobile-bar";
import { RomaHeroRotationSlides } from "@/components/roma/dark-funnel/roma-hero-rotation-slides";
import { RomaReserveSheetButton } from "@/components/roma/dark-funnel/roma-reserve-sheet-button";
import { sortRomaReserveLocations } from "@/components/roma/roma-types";
import type {
  MarketingHomepageCopy,
  RomaLocationCopy,
} from "@/lib/marketing-location-copy";
import type { RestaurantSiteConfig } from "@/lib/restaurant-platform";
import { cn } from "@/lib/utils";

const ROMA_DARK_ASSET_BASE = "/images/roma-dark-funnel";

const romaHeroImages = [
  `${ROMA_DARK_ASSET_BASE}/hero-1.jpg`,
  `${ROMA_DARK_ASSET_BASE}/hero-2.jpg`,
  `${ROMA_DARK_ASSET_BASE}/hero-3.jpg`,
] as const;
// Story block = the neighborhood dining room. Use an interior shot, not another
// storefront (the location cards up top already carry the exterior photos).
const romaStoryWelcomeImage = `${ROMA_DARK_ASSET_BASE}/hero-3.jpg`;
const romaDinnerBannerImage = `${ROMA_DARK_ASSET_BASE}/order-bg.jpg`;
const romaGamingBannerImage = `${ROMA_DARK_ASSET_BASE}/henderson-gaming.jpg`;
// Private events = an elegant private dining room set for a group (not the casual
// TV nook that catering.jpg showed).
const romaPrivateDiningImage = `${ROMA_DARK_ASSET_BASE}/hero-2.jpg`;
const romaClubBannerImage = `${ROMA_DARK_ASSET_BASE}/rewards-bg.jpg`;

const railItems = [
  {
    label: "Meatballs",
    imageSrc: `${ROMA_DARK_ASSET_BASE}/item-meatballs.jpg`,
    imageAlt: "Meatballs",
  },
  {
    label: "Stuffed artichokes",
    imageSrc: `${ROMA_DARK_ASSET_BASE}/item-artichokes.jpg`,
    imageAlt: "Stuffed artichokes",
  },
  {
    label: "Gnocchi",
    imageSrc: `${ROMA_DARK_ASSET_BASE}/item-gnocchi.jpg`,
    imageAlt: "Gnocchi",
  },
  {
    label: "Penne Bolognese",
    imageSrc: `${ROMA_DARK_ASSET_BASE}/item-bolognese.jpg`,
    imageAlt: "Penne Bolognese",
  },
  {
    label: "Chicken Parmigiana",
    imageSrc: `${ROMA_DARK_ASSET_BASE}/item-chicken-parm.jpg`,
    imageAlt: "Chicken Parmigiana",
  },
  {
    label: "Tiramisu",
    imageSrc: `${ROMA_DARK_ASSET_BASE}/item-tiramisu.jpg`,
    imageAlt: "Tiramisu",
  },
] as const;

const galleryImages = [
  {
    src: `${ROMA_DARK_ASSET_BASE}/gallery-1.jpg`,
    alt: "Branzino with lemon and butter",
  },
  {
    src: `${ROMA_DARK_ASSET_BASE}/gallery-2.jpg`,
    alt: "Italian pasta at Chef's Roma Kitchen",
  },
  {
    src: `${ROMA_DARK_ASSET_BASE}/gallery-3.jpg`,
    alt: "New Zealand lamb chops at Chef's Roma Kitchen",
  },
  {
    src: `${ROMA_DARK_ASSET_BASE}/gallery-4.jpg`,
    alt: "Roma Kitchen dining room",
  },
  {
    src: `${ROMA_DARK_ASSET_BASE}/gallery-5.jpg`,
    alt: "Eggplant parmigiana at Chef's Roma Kitchen",
  },
  {
    src: `${ROMA_DARK_ASSET_BASE}/gallery-6.jpg`,
    alt: "Italian wine and dinner table",
  },
  {
    src: `${ROMA_DARK_ASSET_BASE}/gallery-7.jpg`,
    alt: "Private dining table at Chef's Roma Kitchen",
  },
  {
    src: `${ROMA_DARK_ASSET_BASE}/gallery-8.jpg`,
    alt: "Roma Kitchen appetizer",
  },
  {
    src: `${ROMA_DARK_ASSET_BASE}/gallery-9.jpg`,
    alt: "Chef's Roma Kitchen dessert",
  },
] as const;

const HENDERSON_WEEKLY_HOURS = [
  { day: "Sunday", hours: "4:00 PM - 2:00 AM" },
  { day: "Monday", hours: "4:00 PM - 2:00 AM" },
  { day: "Tuesday", hours: "4:00 PM - 2:00 AM" },
  { day: "Wednesday", hours: "4:00 PM - 2:00 AM" },
  { day: "Thursday", hours: "4:00 PM - 2:00 AM" },
  { day: "Friday", hours: "4:00 PM - 2:00 AM" },
  { day: "Saturday", hours: "4:00 PM - 2:00 AM" },
] as const;

const SOUTHERN_HIGHLANDS_WEEKLY_HOURS = [
  { day: "Sunday", hours: "4:00 PM - 8:00 PM" },
  { day: "Monday", hours: "4:00 PM - 9:00 PM" },
  { day: "Tuesday", hours: "4:00 PM - 9:00 PM" },
  { day: "Wednesday", hours: "4:00 PM - 9:00 PM" },
  { day: "Thursday", hours: "4:00 PM - 9:00 PM" },
  { day: "Friday", hours: "4:00 PM - 9:00 PM" },
  { day: "Saturday", hours: "4:00 PM - 9:00 PM" },
] as const;

type RomaDarkFunnelHomepageProps = {
  siteConfig: RestaurantSiteConfig;
  homepageCopy: MarketingHomepageCopy;
};

function isExternalHref(href: string): boolean {
  return /^https?:\/\//.test(href);
}

function externalLinkProps(href: string) {
  return isExternalHref(href)
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};
}

function findLocation(
  locations: readonly RomaLocationCopy[],
  slug: RomaLocationCopy["slug"],
) {
  return locations.find((location) => location.slug === slug);
}

function featureBadgeIcon(label: string): LucideIcon {
  const normalized = label.toLowerCase();

  if (normalized.includes("chef") || normalized.includes("lamb")) {
    return UtensilsCrossed;
  }

  if (normalized.includes("pasta")) {
    return Utensils;
  }

  if (normalized.includes("reservation") || normalized.includes("yelp")) {
    return CalendarCheck;
  }

  if (normalized.includes("private")) {
    return UsersRound;
  }

  if (normalized.includes("catering")) {
    return Truck;
  }

  if (normalized.includes("wine")) {
    return Wine;
  }

  return Star;
}

const FUNNEL_BTN_PRIMARY =
  "inline-flex items-center justify-center min-h-11 rounded-lg border border-[#b91c1c] bg-[#b91c1c] px-5 py-3 text-[0.9375rem] font-semibold leading-tight text-white transition-colors hover:border-[#cf1f1f] hover:bg-[#cf1f1f]";

const FUNNEL_BTN_GHOST =
  "inline-flex items-center justify-center min-h-11 rounded-lg border border-white/26 bg-transparent px-5 py-3 text-[0.9375rem] font-semibold leading-tight text-white transition-colors hover:border-white/40 hover:bg-white/8";

function FunnelPrimaryButton({
  href,
  children,
  className,
  trackLocation,
  trackLabel,
  trackEvent = "reservation_clicked",
}: {
  href: string;
  children: ReactNode;
  className?: string;
  trackLocation: string;
  trackLabel?: string;
  trackEvent?: string;
}) {
  const props = externalLinkProps(href);
  const shared = {
    className: cn("roma-funnel-btn-primary", FUNNEL_BTN_PRIMARY, className),
    "data-track": trackEvent,
    "data-track-location": trackLocation,
    ...(trackLabel ? { "data-track-label": trackLabel } : {}),
  };

  if (isExternalHref(href)) {
    return (
      <a href={href} {...props} {...shared}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} prefetch={false} {...shared}>
      {children}
    </Link>
  );
}

function FunnelGhostButton({
  href,
  children,
  className,
  trackLocation,
  trackEvent = "menu_clicked",
}: {
  href: string;
  children: ReactNode;
  className?: string;
  trackLocation: string;
  trackEvent?: string;
}) {
  const props = externalLinkProps(href);

  if (isExternalHref(href)) {
    return (
      <a
        href={href}
        {...props}
        className={cn("roma-funnel-btn-ghost", FUNNEL_BTN_GHOST, className)}
        data-track={trackEvent}
        data-track-location={trackLocation}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      href={href}
      prefetch={false}
      className={cn("roma-funnel-btn-ghost", FUNNEL_BTN_GHOST, className)}
      data-track={trackEvent}
      data-track-location={trackLocation}
    >
      {children}
    </Link>
  );
}

export function RomaDarkFunnelHomepage({
  siteConfig,
  homepageCopy,
}: RomaDarkFunnelHomepageProps) {
  const locations = homepageCopy.romaLocations ?? [];
  const sortedReserveLocations = sortRomaReserveLocations(locations);
  const southernHighlands = findLocation(locations, "southern-highlands");
  const henderson = findLocation(locations, "henderson");
  const privateDining = homepageCopy.privateDining;

  // Aggregate Google review trust signal, derived from the per-location data so
  // the numbers stay in sync with marketing-location-copy (not hardcoded here).
  const totalGoogleReviews = locations.reduce(
    (sum, location) => sum + location.googleReviewCount,
    0,
  );
  const weightedGoogleRating =
    totalGoogleReviews > 0
      ? Math.round(
          (locations.reduce(
            (sum, location) =>
              sum + location.googleRating * location.googleReviewCount,
            0,
          ) /
            totalGoogleReviews) *
            10,
        ) / 10
      : 0;
  const roundedGoogleReviewCount = Math.floor(totalGoogleReviews / 10) * 10;

  // Day-of-week in America/Los_Angeles so a Las Vegas restaurant shows the
  // correct "Today" hours near midnight regardless of where the server
  // renders. Computed once here; the Intl call is identical per location.
  const laWeekday = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    timeZone: "America/Los_Angeles",
  });

  return (
    <div
      className="roma-paper-dark min-h-screen bg-[#0d0d0d] pb-[calc(4.5rem+env(safe-area-inset-bottom))] text-white md:pb-0"
      data-homepage
      data-homepage-brand={homepageCopy.brand}
      data-homepage-funnel="dark"
    >
      <HomepageAnalytics />

      {/* Hero — dual reserve CTAs, no desktop reservation widget */}
      <section
        className="roma-funnel-hero relative -mt-site-header isolate min-h-[min(84svh,720px)] overflow-hidden pt-site-header md:min-h-[min(92svh,760px)]"
        aria-labelledby="roma-dark-hero-heading"
      >
        <div className="absolute inset-0" aria-hidden="true">
          {/*
            LCP: slide 1 is a prioritized next/image base layer (preloaded +
            fetchpriority=high + optimized) that is always visible at opacity 1,
            so there is no black flash before the funnel toggle and the first
            paint has a real, fast hero image. Slides 2-3 mount as optimized
            next/image renders only after window load (raw CSS backgrounds
            downloaded ~770KB of full-size JPGs at High priority and pushed
            mobile LCP past 8s).
          */}
          <Image
            src={romaHeroImages[0]}
            alt=""
            fill
            priority
            fetchPriority="high"
            sizes="100vw"
            quality={70}
            className="object-cover object-center"
          />
          <RomaHeroRotationSlides images={romaHeroImages} />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.82)_0%,rgba(0,0,0,0.52)_48%,rgba(0,0,0,0.12)_78%),linear-gradient(0deg,rgba(0,0,0,0.78)_0%,rgba(0,0,0,0.28)_42%,rgba(0,0,0,0)_68%)]" />
        </div>

        <div className="relative z-10 mx-auto flex min-h-[calc(min(84svh,720px)-var(--site-header-height))] max-w-[1200px] flex-col justify-end px-4 pb-[calc(7rem+env(safe-area-inset-bottom))] pt-16 sm:px-8 md:min-h-[calc(min(92svh,760px)-var(--site-header-height))] md:pb-12 lg:px-12 lg:pb-16">
          {/* Entrance stagger animates opacity/transform only (fade-up, 0.6s),
              so the prioritized hero image stays the untouched LCP element. */}
          <h1
            id="roma-dark-hero-heading"
            className="animate-hero-1 max-w-[14ch] text-balance font-serif text-[clamp(2.1rem,5vw,3.5rem)] font-bold leading-[1.02] tracking-[-0.01em] text-white [text-shadow:0_2px_28px_rgba(0,0,0,0.55)]"
          >
            {homepageCopy.heroTitle}
          </h1>
          <p className="animate-hero-2 mt-4 max-w-[38ch] text-pretty text-base leading-relaxed text-white/90 [text-shadow:0_1px_18px_rgba(0,0,0,0.5)] sm:text-lg">
            {homepageCopy.answerFirst}
          </p>
          <div className="animate-hero-3 mt-6 flex max-w-[420px] flex-col items-start gap-3">
            <RomaReserveSheetButton
              fallbackHref={
                (southernHighlands ?? henderson)?.reserveUrl ??
                siteConfig.menuUrl
              }
              trackLocation="home_dark_funnel_hero"
              className={cn(
                "roma-funnel-btn-primary",
                FUNNEL_BTN_PRIMARY,
                "min-h-12 w-full px-3.5 py-3 text-center text-[0.9rem] leading-snug",
              )}
            >
              Reserve a table
            </RomaReserveSheetButton>
            <FunnelGhostButton
              href={siteConfig.menuUrl}
              trackLocation="home_dark_funnel_hero_menu"
              className="min-h-[46px] w-full sm:w-auto sm:min-w-[168px]"
            >
              View the menu
            </FunnelGhostButton>
          </div>
          {totalGoogleReviews > 0 ? (
            <p className="animate-hero-4 mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/85">
              <Star
                className="h-4 w-4 shrink-0 fill-current text-[#f5b301]"
                aria-hidden="true"
              />
              <span className="font-semibold text-white">
                {weightedGoogleRating.toFixed(1)}
              </span>
              <span aria-hidden="true" className="text-white/40">
                ·
              </span>
              <span>{roundedGoogleReviewCount}+ Google reviews</span>
            </p>
          ) : null}
        </div>
      </section>

      <div id="home-mobile-sticky-boundary" aria-hidden="true" />

      {/* Full locations */}
      <section id="location" className="scroll-mt-site-header px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1200px]">
          <h2 className="reveal font-serif text-[clamp(1.85rem,4vw,2.35rem)] font-bold leading-tight">
            {homepageCopy.romaLocationsHeading ?? "Our locations"}
          </h2>
          <div className="mt-8 grid gap-8 lg:grid-cols-2">
            {[henderson, southernHighlands].filter(Boolean).map((location, index) => {
              const weeklyHours =
                location!.slug === "henderson"
                  ? HENDERSON_WEEKLY_HOURS
                  : SOUTHERN_HIGHLANDS_WEEKLY_HOURS;
              const laDayIndex = weeklyHours.findIndex(
                (entry) => entry.day === laWeekday
              );
              const todayHours =
                weeklyHours[laDayIndex >= 0 ? laDayIndex : 0]?.hours ??
                weeklyHours[0]?.hours;

              return (
                <article
                  key={location!.slug}
                  id={`roma-location-${location!.slug}`}
                  className={cn(
                    "reveal overflow-hidden rounded-[14px] border border-[var(--border)] bg-[var(--card)]",
                    index === 0 ? "reveal-delay-1" : "reveal-delay-2",
                  )}
                >
                  <a
                    href={location!.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative block aspect-[16/9] overflow-hidden bg-[var(--muted)]"
                    data-track="directions_clicked"
                    data-track-location="home_dark_funnel_locations"
                    data-track-label={location!.slug}
                  >
                    <Image
                      src={location!.imageSrc}
                      alt={location!.imageAlt}
                      fill
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      quality={75}
                      className="object-cover object-center"
                    />
                  </a>
                  <div className="space-y-5 p-5 sm:p-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-[var(--muted-foreground)]">
                          {location!.slug === "henderson"
                            ? "Henderson · Horizon Ridge"
                            : "Southern Highlands · Las Vegas"}
                        </p>
                        <h3 className="mt-1 font-serif text-2xl font-bold">{location!.name}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-[var(--muted-foreground)]">
                          {location!.noteLine}
                        </p>
                      </div>
                      <FunnelPrimaryButton
                        href={location!.reserveUrl}
                        trackLocation="home_dark_funnel_locations_reserve"
                        trackLabel={location!.slug}
                        className="shrink-0 self-start px-6"
                      >
                        Reserve
                      </FunnelPrimaryButton>
                    </div>

                    <div className="grid gap-4 border-t border-[var(--border)] pt-4 text-sm sm:grid-cols-2">
                      <div>
                        <h4 className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-[var(--muted-foreground)]">
                          Address
                        </h4>
                        <p className="mt-2 leading-relaxed">
                          {location!.addressLine1}
                          <br />
                          {location!.addressLine2}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-[var(--muted-foreground)]">
                          Reservations
                        </h4>
                        <p className="mt-2 leading-relaxed">
                          <a
                            href={`tel:${location!.phoneHref}`}
                            className="hover:text-[var(--primary)]"
                            data-track="phone_clicked"
                            data-track-location="home_dark_funnel_locations"
                            data-track-label={location!.slug}
                          >
                            {location!.phoneDisplay}
                          </a>
                          <br />
                          Book a table
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-4 border-t border-[var(--border)] pt-4 sm:flex-row sm:items-center sm:justify-between">
                      <details className="group">
                        <summary className="flex cursor-pointer list-none items-center gap-2 text-sm marker:hidden">
                          <span>
                            <strong>Today:</strong> {todayHours}
                          </span>
                          <span className="text-[var(--muted-foreground)] underline-offset-4 group-open:hidden hover:underline">
                            See hours
                          </span>
                        </summary>
                        <ul className="mt-3 space-y-1.5 text-sm text-[var(--muted-foreground)]">
                          {weeklyHours.map((entry) => (
                            <li key={entry.day} className="flex justify-between gap-4">
                              <span>{entry.day}</span>
                              <span>{entry.hours}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                      <FunnelGhostButton
                        href={location!.mapsUrl}
                        trackLocation="home_dark_funnel_locations_directions"
                        trackEvent="directions_clicked"
                        className="shrink-0 px-4 py-2 text-sm"
                      >
                        Get directions
                      </FunnelGhostButton>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured menu rail */}
      <section id="featured" className="border-t border-[var(--border)] px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1200px]">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="reveal font-serif text-[clamp(1.85rem,4vw,2.5rem)] font-bold leading-tight">
                The plates people come back for.
              </h2>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-[var(--muted-foreground)]">
                {homepageCopy.menuPreviewDescription}
              </p>
            </div>
            <FunnelGhostButton
              href={siteConfig.menuUrl}
              trackLocation="home_dark_funnel_featured"
              className="shrink-0 self-start"
            >
              View the menu
            </FunnelGhostButton>
          </div>

          <div className="roma-funnel-featured-rail-wrap relative -mx-4 px-4 sm:mx-0 sm:px-0">
            <div className="roma-funnel-featured-rail flex snap-x snap-mandatory gap-3 overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x pb-2 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-4 [&::-webkit-scrollbar]:hidden">
              {railItems.map((item) => (
                <article
                  key={item.label}
                  // Fixed card width leaves a clear peek of the next plate so
                  // the rail reads as swipeable. Do not use `.reveal` here —
                  // opacity:0 on the clipped card hid the peek entirely.
                  className="w-[148px] shrink-0 snap-start sm:w-[190px]"
                >
                  <div className="relative aspect-square overflow-hidden rounded-[14px] bg-[var(--card)]">
                    <Image
                      src={item.imageSrc}
                      alt={item.imageAlt}
                      fill
                      sizes="190px"
                      quality={75}
                      className="object-cover object-center"
                    />
                  </div>
                  <p className="mt-2 text-sm font-medium text-[var(--foreground)]">
                    {item.label}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Story block */}
      <section id="welcome" className="border-t border-[var(--border)] px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1200px]">
          <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-16">
            <div className="relative aspect-[4/3] overflow-hidden rounded-[14px] bg-[var(--card)]">
              <Image
                src={romaStoryWelcomeImage}
                alt="The dining room at Chef's Roma Kitchen"
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                quality={75}
                className="object-cover object-center"
              />
            </div>
            <div>
              <h2 className="reveal font-serif text-[clamp(1.85rem,4vw,2.35rem)] font-bold leading-tight">
                {homepageCopy.guestSectionTitle}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted-foreground)]">
                {homepageCopy.guestSectionDescription}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dinner banner */}
      <section className="roma-funnel-banner relative overflow-hidden" aria-label="Dinner service">
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <Image
            src={romaDinnerBannerImage}
            alt=""
            fill
            sizes="100vw"
            quality={70}
            className="object-cover object-center transition-transform duration-700 hover:scale-105"
          />
        </div>
        <div
          className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.72)_0%,rgba(0,0,0,0.45)_100%)]"
          aria-hidden="true"
        />
        <div className="relative z-10 mx-auto flex max-w-[1200px] justify-end px-4 py-16 sm:px-8 lg:px-12 lg:py-20">
          <div className="w-full max-w-[460px] rounded-[18px] border border-white/14 bg-[color-mix(in_srgb,var(--card)_88%,transparent)] p-6 backdrop-blur-sm sm:p-8">
            <h2 className="reveal font-serif text-[clamp(1.75rem,4vw,2.25rem)] font-bold leading-tight">
              Dinner from 4pm, every night.
            </h2>
            <p className="mt-3 max-w-[34ch] text-base leading-relaxed text-[var(--muted-foreground)]">
              Two locations, one wine list. Pick the closest and lock in tonight&apos;s table.
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <div className="grid gap-2.5 sm:grid-cols-2">
                {southernHighlands ? (
                  <FunnelPrimaryButton
                    href={southernHighlands.reserveUrl}
                    trackLocation="home_dark_funnel_dinner_banner"
                    trackLabel="southern-highlands"
                    className="w-full text-center"
                  >
                    Reserve Southern Highlands
                  </FunnelPrimaryButton>
                ) : null}
                {henderson ? (
                  <FunnelPrimaryButton
                    href={henderson.reserveUrl}
                    trackLocation="home_dark_funnel_dinner_banner"
                    trackLabel="henderson"
                    className="w-full text-center"
                  >
                    Reserve Henderson
                  </FunnelPrimaryButton>
                ) : null}
              </div>
              <a
                href="#location"
                className="text-sm text-[var(--muted-foreground)] underline-offset-4 hover:text-[var(--foreground)] hover:underline"
              >
                Compare both locations
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery. Also the incentive-popup trigger anchor: IncentivePopup arms
          on first scroll and opens once 15% of this section enters the viewport,
          mirroring the light homepage where the same attribute wraps the
          popular-plates gallery. */}
      <section
        id="gallery"
        data-incentive-popup-trigger="popular-gallery"
        className="px-4 py-12 sm:px-8 sm:py-16 lg:px-12"
      >
        <div className="mx-auto max-w-[1200px]">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-2xl">
              <h2 className="reveal font-serif text-[clamp(1.85rem,4vw,2.35rem)] font-bold leading-tight">
                {homepageCopy.featureHeading}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-[var(--muted-foreground)]">
                The full menu covers primi, secondi, dessert, and the wine list.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-3 self-start">
              <FunnelGhostButton
                href={siteConfig.menuUrl}
                trackLocation="home_dark_funnel_gallery"
              >
                View the menu
              </FunnelGhostButton>
              <FunnelGhostButton
                href="/best"
                trackLocation="home_dark_funnel_local_guides"
              >
                Local dining guides
              </FunnelGhostButton>
            </div>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-3.5">
            {galleryImages.map((image, index) => (
              <figure
                key={`${image.src}-${image.alt}`}
                // Light column-based stagger (repeats every grid row).
                className={cn(
                  "reveal relative aspect-square overflow-hidden rounded-[14px] bg-[var(--card)] [&:nth-child(n+7)]:hidden sm:[&:nth-child(n+7)]:block [&:nth-child(9)]:hidden lg:[&:nth-child(9)]:block",
                  `reveal-delay-${(index % 3) + 1}`,
                )}
              >
                <Link
                  href={siteConfig.menuUrl}
                  prefetch={false}
                  aria-label={`View the menu: ${image.alt}`}
                  className="absolute inset-0 block"
                  data-track="menu_clicked"
                  data-track-location="home_dark_funnel_gallery"
                >
                  <Image
                    src={image.src}
                    alt={image.alt}
                    fill
                    sizes="(max-width: 640px) 45vw, 33vw"
                    quality={70}
                    className="object-cover object-center transition-transform duration-500 hover:scale-105"
                  />
                </Link>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Henderson gaming band */}
      <section
        className="roma-funnel-banner relative overflow-hidden"
        aria-label="Gaming at the Henderson location"
      >
        <div className="absolute inset-0" aria-hidden="true">
          <Image
            src={romaGamingBannerImage}
            alt=""
            fill
            sizes="100vw"
            quality={70}
            className="object-cover object-center transition-transform duration-700 hover:scale-105"
          />
          {/* Uniform base scrim keeps full-width mobile text legible over the
              busy gaming image; the directional layer darkens the left for the
              desktop composition. */}
          <div className="absolute inset-0 bg-[color-mix(in_srgb,var(--background)_52%,transparent)]" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,var(--background)_0%,color-mix(in_srgb,var(--background)_68%,transparent)_48%,transparent_84%)]" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1200px] px-4 py-16 sm:px-8 lg:px-12 lg:py-20">
          <div className="max-w-[560px]">
            <p className="text-[0.8rem] font-semibold uppercase tracking-[0.09em] text-[#d6d6d6] [text-shadow:0_1px_8px_rgba(0,0,0,0.7)]">
              Late night · Henderson only
            </p>
            <h2 className="reveal mt-3 font-serif text-[clamp(1.85rem,4vw,2.5rem)] font-bold leading-tight text-white [text-shadow:0_2px_16px_rgba(0,0,0,0.65)]">
              Late Night. Play. Eat.
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-white/90 [text-shadow:0_1px_12px_rgba(0,0,0,0.7)]">
              Open till 2&nbsp;AM. Walk in.
            </p>
            <div className="mt-6 flex flex-col items-start gap-3">
              <FunnelPrimaryButton
                href="/gaming"
                trackLocation="home_dark_funnel_gaming_band"
                trackLabel="henderson"
                trackEvent="cta_clicked"
                className="inline-flex"
              >
                Late night gaming
              </FunnelPrimaryButton>
              {henderson?.mapsUrl ? (
                <a
                  href={henderson.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[var(--muted-foreground)] underline-offset-4 [text-shadow:0_1px_8px_rgba(0,0,0,0.7)] hover:text-[var(--foreground)] hover:underline"
                  data-track="directions_clicked"
                  data-track-location="home_dark_funnel_gaming_band"
                  data-track-label="henderson"
                >
                  Get directions
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Private dining */}
      {privateDining ? (
        <section id="private-events" className="px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
          <div className="mx-auto grid max-w-[1200px] items-center gap-8 lg:grid-cols-2 lg:gap-16">
            <div className="relative aspect-[4/3] overflow-hidden rounded-[14px] bg-[var(--card)] lg:order-2">
              <Image
                src={romaPrivateDiningImage}
                alt={privateDining.imageAlt}
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                quality={75}
                className="object-cover object-center"
              />
            </div>
            <div className="lg:order-1">
              <h2 className="reveal font-serif text-[clamp(1.85rem,4vw,2.35rem)] font-bold leading-tight">
                {privateDining.title}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted-foreground)]">
                {privateDining.description}
              </p>
              <ul className="mt-5 list-disc space-y-2 pl-5 text-[var(--muted-foreground)]">
                {privateDining.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <FunnelPrimaryButton
                  href={privateDining.ctaHref}
                  trackLocation="home_dark_funnel_private_dining"
                  trackEvent="cta_clicked"
                  className="inline-flex"
                >
                  {privateDining.ctaLabel}
                </FunnelPrimaryButton>
                <FunnelGhostButton
                  href={privateDining.secondaryCtaHref}
                  trackLocation="home_dark_funnel_catering"
                  trackEvent="cta_clicked"
                >
                  {privateDining.secondaryCtaLabel}
                </FunnelGhostButton>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* Google reviews */}
      <section id="reviews" className="px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1200px]">
          <div className="rounded-[18px] border border-[var(--border)] bg-[var(--card)] p-6 sm:p-8">
            <h2 className="reveal font-serif text-[clamp(1.85rem,4vw,2.35rem)] font-bold leading-tight">
              {/* Lead with the aggregate rating (computed above from per-location
                  GBP data) instead of the shared reviewsHeading; the shared
                  string stays untouched for other consumers. */}
              {totalGoogleReviews > 0
                ? `${weightedGoogleRating.toFixed(1)} on Google. What guests notice.`
                : homepageCopy.reviewsHeading}
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {homepageCopy.featuredReviews.slice(0, 4).map((review, index) => (
                <article
                  key={`${review.source}-${review.meta}`}
                  className={cn(
                    "reveal rounded-[14px] border border-[var(--border)] bg-[var(--background)] p-5",
                    `reveal-delay-${index + 1}`,
                  )}
                >
                  <p
                    role="img"
                    aria-label={review.eyebrow}
                    className="flex items-center gap-0.5 text-[var(--roma-star)]"
                  >
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star key={index} className="h-4 w-4 fill-current" aria-hidden="true" />
                    ))}
                  </p>
                  <p className="mt-3 font-semibold text-[var(--foreground)]">{review.source}</p>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--muted-foreground)]">
                    {review.excerpt}
                  </p>
                  {review.meta ? (
                    <p className="mt-4 text-xs uppercase tracking-[0.12em] text-[var(--muted-foreground)]">
                      {review.meta}
                    </p>
                  ) : null}
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featuring */}
      <section id="featuring" className="px-4 py-12 text-center sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1200px]">
          <h2 className="reveal font-serif text-[clamp(1.85rem,4vw,2.35rem)] font-bold leading-tight">
            What Roma is known for
          </h2>
          <div className="mt-8 grid grid-cols-2 justify-items-center gap-x-4 gap-y-6 sm:flex sm:flex-wrap sm:justify-center sm:gap-x-12 sm:gap-y-8 lg:gap-x-16">
            {homepageCopy.featureBadges.map((badge) => {
              const Icon = featureBadgeIcon(badge);

              return (
                <div
                  key={badge}
                  className="flex max-w-[9rem] flex-col items-center gap-2 text-sm font-medium text-[var(--foreground)]/88"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)] bg-white/5 text-[var(--primary)]"
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <span>{badge}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Roma Club banner */}
      <section id="rewards" className="roma-funnel-banner relative overflow-hidden" aria-label="Roma Club">
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <Image
            src={romaClubBannerImage}
            alt=""
            fill
            sizes="100vw"
            quality={70}
            className="object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.15),rgba(0,0,0,0.45))]" aria-hidden="true" />
        <div className="relative z-10 mx-auto flex max-w-[1200px] justify-end px-4 py-16 sm:px-8 lg:px-12 lg:py-20">
          <div className="w-full max-w-[520px] rounded-[18px] border border-white/14 bg-[color-mix(in_srgb,var(--card)_92%,transparent)] p-4 backdrop-blur-sm sm:p-6">
            <p className="text-sm font-semibold uppercase tracking-[0.12em] text-white/85">
              {homepageCopy.inlineOffer.eyebrow}
            </p>
            <h2 className="mt-2 font-serif text-[clamp(1.35rem,3.5vw,1.75rem)] font-bold leading-tight">
              {homepageCopy.inlineOffer.heading}
            </h2>
            <p className="mt-3 text-base leading-relaxed text-[var(--muted-foreground)]">
              {homepageCopy.inlineOffer.description}
            </p>
            <HomeInlineOfferCapture
              layout="embedded"
              theme="dark-funnel"
              source={homepageCopy.inlineOffer.source}
              submitLabel={homepageCopy.inlineOffer.submitLabel}
              privacyNote={homepageCopy.inlineOffer.privacyNote}
              successEyebrow={homepageCopy.inlineOffer.successEyebrow}
              successHeading={homepageCopy.inlineOffer.successHeading}
              successDescription={homepageCopy.inlineOffer.successDescription}
            />
          </div>
        </div>
      </section>

      <HomeCompactFaq
        className="!bg-[var(--background)] !text-[var(--foreground)] [&_h2]:!text-white [&_summary]:!text-white [&_summary_span:last-child]:!border-white/14 [&_summary_span:last-child]:!text-[#ef6a5f] [&_p]:!text-[#d6d6d6] [&_.divide-y]:!divide-white/10 [&_.border-y]:!border-white/10"
        items={homepageCopy.faqItems}
      />

      <RomaDarkFunnelMobileBar locations={sortedReserveLocations} />
    </div>
  );
}

export default RomaDarkFunnelHomepage;
